import JSZip from 'jszip';
import { enqueueSnackbar } from '~/utils/notifications';
import { SubstitutionLog } from '~/domain/dicom/engine/charset';
import { PatientIdentityNarrowingError } from '~/domain/dicom/engine/writer';
import { useState } from 'react';
import {
    UploadDICOMRequestBody,
    useUploadDICOM,
} from '~/backend/connector-api/connector-components';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import {
    ensureStructureSetsCurrent,
    type LabelledStructureSet,
} from '~/hooks/ensureStructureSetsCurrent';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { handleError } from '~/utils/errors';
import { emitSubstitutionToast } from '~/utils/io/files/substitutionReportToast';
import {
    buildRegistrationFilename,
    buildRtDoseFilename,
    buildRtPlanFilename,
    buildStructureSetFilename,
} from '~/utils/io/files/dicomFilename';
import {
    downloadImageToDisk,
    saveBlobToFile,
} from '~/utils/io/files/downloadDicom';
import {
    buildOptimizationObjectivesCsv,
    buildOptimizationObjectivesFilename,
    buildOptimizationObjectivesJson,
} from '~/utils/io/files/optimizationObjectives';
import useSelectedPatient from '../patient/useSelectedPatient';
import { redactDicomUids } from '~/utils/phiRedaction';
import type { RoiId } from '~/domain/changes/ids';

/**
 * Identifies a structure set to export and the optional subset of ROIs
 * to include. Keyed by SOP Instance UID — the natural key the
 * {@link StructureSetExportService} already works with. The live domain
 * entity is resolved inside the service, never handled by the hook.
 */
/**
 * The message shown when an export could not be written.
 *
 * A refusal to narrow patient identity is actionable in a way a generic
 * failure is not: it names the attribute the chosen character set cannot
 * carry, and repeating the export will not change that. Saying "try again"
 * invites exactly the one thing that cannot work.
 *
 * The attribute keywords are dictionary terms and carry no patient data. The
 * values do, and stay out of the message — it is logged.
 */
function exportFailureMessage(error: unknown, fallback: string): string {
    if (!(error instanceof PatientIdentityNarrowingError)) {
        return fallback;
    }
    const attributes = error.attributes.map((a) => a.keyword).join(' and ');
    return (
        `Export cancelled: ${error.charset} cannot represent this patient's ` +
        `${attributes}. Export in a character set that can carry it, or ` +
        'change the value.'
    );
}

export type SelectedStructureSetsWithStructures = {
    /** SOP Instance UID of the structure set to export. */
    structureSetUid: string;
    /**
     * Structures to include. When undefined the whole structure set is
     * exported.
     */
    selectedRoiIds?: RoiId[];
};

/**
 * Extracts the DICOM dose and plan blobs from a DoseMatrix.
 */
const getDoseBlobs = (
    dose: DoseMatrix
): { doseBlob?: Blob; planBlob?: Blob } => ({
    doseBlob: dose.dicomDose.getAsDicomBlob(),
    planBlob: dose.plan?.dicom?.getAsDicomBlob(),
});

export const useDicomExport = () => {
    const { mutateAsync: uploadDICOM } = useUploadDICOM();
    const selectedPatient = useSelectedPatient();
    const [exporting, setExporting] = useState(false);
    const session = useSessionOrNull();

    /**
     * {@link ensureStructureSetsCurrent} for the structure sets with the
     * given SOP Instance UIDs, flushing every grid resident. False means the
     * export is refused and the user has been told why.
     */
    const ensureSetsWithUidsCurrent = (
        structureSetUids: readonly string[]
    ): boolean =>
        ensureStructureSetsCurrent(
            getDatasetEnvironmentRegistry(),
            structureSetUids.flatMap((uid): LabelledStructureSet[] => {
                const ss =
                    session?.patient.getStructureSetWithSopInstanceUid(uid);
                return ss ? [ss] : [];
            }),
            'Export'
        );

    /**
     * Produces a DICOM RTSTRUCT blob for a structure set, optionally
     * limited to a subset of ROIs.
     *
     * Blob production is delegated to {@link StructureSetExportService};
     * the hook never touches the live `StructureSet` entity. The blob
     * reflects the domain as it stands, so
     * {@link ensureSetsWithUidsCurrent} must run first.
     */
    const structureSetToBlob = (
        structureSetUid: string,
        selectedRoiIds?: RoiId[],
        exportToMonaco?: boolean
    ): Blob => {
        if (!session) {
            throw new Error('Internal error, patient session not found');
        }
        const substitutions = new SubstitutionLog();
        const blob = session.structureSetExportService.exportBlob(
            structureSetUid,
            {
                selectedRoiIds,
                characterSet: exportToMonaco ? 'ISO_IR 100' : undefined,
                substitutions,
            }
        );
        emitSubstitutionToast(substitutions);
        return blob;
    };

    /**
     * Produces a Monaco DIR file blob for a structure set.
     *
     * Exports the structure set in a format compatible with Monaco,
     * allowing for easy sharing and collaboration. Blob production is
     * delegated to {@link StructureSetExportService}, and
     * {@link ensureSetsWithUidsCurrent} must run first.
     *
     * @param {string} structureSetUid - SOP Instance UID of the structure set.
     * @returns {Blob} A Blob representing the exported structure set in Monaco format.
     */
    const monacoStructureSetToBlob = (structureSetUid: string): Blob => {
        if (!session) {
            throw new Error('Internal error, patient session not found');
        }
        return session.structureSetExportService.exportBlob(structureSetUid, {
            asMonacoFile: true,
            patientName: selectedPatient?.dataSet.PatientName ?? 'NO_NAME',
        });
    };

    const uploadDicomBlob = async (
        blob: Blob,
        filename: string,
        targetName: string
    ): Promise<void> => {
        const formData = new FormData();
        formData.append('files', blob, filename);
        await uploadDICOM({
            body: formData as unknown as UploadDICOMRequestBody,
            pathParams: { target: targetName },
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    };

    /**
     * Downloads a DICOM Image as a ZIP file.
     *
     * This function extracts all DICOM slices from an Image3D object,
     * combines them into a ZIP archive, and downloads it.
     *
     * @param {Image3D} image - The image to be downloaded.
     * @returns {Promise<void>} A promise that resolves when the download completes.
     */
    const handleDownloadImage = async (image: Image3D): Promise<void> => {
        setExporting(true);
        await downloadImageToDisk(image);
        setExporting(false);
    };

    /**
     * Resolves the structure set label used in export file names.
     *
     * The {@link StructureSetExportService} owns blob production but the
     * file name is a UI concern, so the hook reads the label from the
     * patient. This does not re-expose the live entity beyond a single
     * field read.
     */
    const getStructureSetLabel = (structureSetUid: string): string => {
        const ss =
            session?.patient.getStructureSetWithSopInstanceUid(structureSetUid);
        if (!ss) {
            throw new Error(`Structure set not found: ${structureSetUid}`);
        }
        return ss.structureSetLabel;
    };

    /**
     * Downloads a DICOM Structure Set file.
     *
     * This function exports a Structure Set either with a subset of selected ROIs
     * or the entire structure set, then saves it as a `.dcm` file.
     *
     * @param {string} structureSetUid - SOP Instance UID of the structure set to export.
     * @param {RoiId[]} [selectedRoiIds] - Optional structures to include in the export.
     *                                  If omitted, the entire structure set is exported.
     * @returns {Promise<void>} A promise that resolves when the export process completes.
     */
    const handleDownloadStructureSet = async (
        structureSetUid: string,
        selectedRoiIds?: RoiId[]
    ): Promise<void> => {
        try {
            setExporting(true);
            if (!ensureSetsWithUidsCurrent([structureSetUid])) {
                return;
            }

            const structureSetLabel = getStructureSetLabel(structureSetUid);
            const structureSetBlob = structureSetToBlob(
                structureSetUid,
                selectedRoiIds
            );

            if (!structureSetBlob) {
                throw new Error('Failed to generate Structure Set Blob');
            }

            saveBlobToFile(
                structureSetBlob,
                buildStructureSetFilename(structureSetLabel, structureSetUid)
            );
        } catch (error) {
            handleError(
                error,
                exportFailureMessage(
                    error,
                    'Exporting files failed, please try again.'
                )
            );
        } finally {
            setExporting(false);
        }
    };

    /**
     * Marks a structure set as exported via the tracked service so the
     * version-cached read models (export dialog isNew/isExported flags)
     * invalidate. A missing structure set throws — silently skipping
     * would leave an uploaded set presenting as never sent.
     */
    const markStructureSetExported = (structureSetUid: string): void => {
        const ss =
            session?.patient.getStructureSetWithSopInstanceUid(structureSetUid);
        if (!session || !ss) {
            throw new Error(`Structure set not found: ${structureSetUid}`);
        }
        session.structureSetService.markExported({ id: ss.id });
    };

    /**
     * Marks a registration as exported via the tracked service. A direct
     * `registration.setExported()` would throw "Mutation outside
     * command()/batch()" because the entity mutator calls
     * `tracker.markChanged`; routing through the service wraps it in a
     * command() and invalidates the version-cached export read models.
     * A missing registration / session throws (an uploaded registration
     * presenting as never sent would be worse).
     */
    const markRegistrationExported = (
        registration: RigidRegistration
    ): void => {
        if (!session) {
            throw new Error('No active session to mark registration exported.');
        }
        session.registrationService.setExported({ id: registration.id });
    };

    /**
     * Fails fast when there is no session to record an export against.
     *
     * Call this BEFORE uploading anything. The mark-exported helpers throw,
     * and if that throw happens after the upload it is caught by the
     * caller's try/catch and surfaces as "Failed to upload" — even though
     * every byte reached the destination. Reporting a successful transfer
     * as a failure is the worse error: the user re-exports, and the item
     * stays flagged as never sent.
     */
    const assertSessionForExport = (): void => {
        if (!session) {
            throw new Error(
                'No active patient session; cannot record the export.'
            );
        }
    };

    /**
     * Marks an image as exported through the tracked service — same reason
     * as {@link markRegistrationExported}: the entity mutator calls
     * `tracker.markChanged` and would throw outside a command().
     */
    const markImageExported = (image: Image3D): void => {
        assertSessionForExport();
        session!.imageService.setExported({ id: image.id });
    };

    /**
     * Marks a dose — and the plan it belongs to — as exported through the
     * tracked service.
     *
     * The plan is marked inside the same batch so a dose+plan export
     * commits as one changeset; a plan is only ever exported alongside its
     * dose, so it needs no service of its own.
     */
    const markDoseExported = (dose: DoseMatrix): void => {
        assertSessionForExport();
        session!.context.tracker.batch(() => {
            session!.doseService.setExported({ id: dose.id });
            dose.plan?.setExported();
        });
    };

    /**
     * Downloads a Dose Matrix as DICOM files.
     *
     * This function extracts the DICOM dose and plan from a Dose Matrix
     * and saves them as `.dcm` files.
     *
     * @param {DoseMatrix} dose - The dose matrix to be downloaded.
     * @returns {Promise<void>} A promise that resolves when the download completes.
     */
    const handleDownloadDose = async (dose: DoseMatrix): Promise<void> => {
        try {
            setExporting(true);
            const { doseBlob, planBlob } = getDoseBlobs(dose);
            if (doseBlob) {
                saveBlobToFile(
                    doseBlob,
                    buildRtDoseFilename(dose.plan?.label ?? dose.dicomUid)
                );
            }
            if (planBlob) {
                saveBlobToFile(
                    planBlob,
                    buildRtPlanFilename(dose.plan?.label ?? dose.dicomUid)
                );
            }
        } catch (error) {
            handleError(
                error,
                'Downloading dose files failed, please try again.'
            );
        } finally {
            setExporting(false);
        }
    };

    /**
     * Downloads a DICOM Registration file.
     *
     * @param {RigidRegistration} registration - The registration to be downloaded.
     * @returns {Promise<void>} A promise that resolves when the download completes.
     */
    const handleDownloadRegistration = async (
        registration: RigidRegistration
    ): Promise<void> => {
        try {
            setExporting(true);
            registration.syncTransformationToDataset();
            const blob = registration.dicomParent.getAsDicomBlob();
            const label = registration.contentLabel || registration.dicomUid;
            saveBlobToFile(blob, buildRegistrationFilename(label));
        } catch (error) {
            handleError(
                error,
                'Downloading registration file failed, please try again.'
            );
        } finally {
            setExporting(false);
        }
    };

    /**
     * Downloads multiple selected `StructureSet`, `DoseMatrix`, and `Image3D` as a batch.
     *
     * If there's only one item, it will download it directly.
     * If there are multiple items, it will combine them into a ZIP archive.
     *
     * @param {SelectedStructureSetsWithStructures[]} selectedStructureSets -
     *        An array of objects identifying a structure set by SOP UID and its selected structures.
     * @param {DoseMatrix[]} selectedDoses -
     *        An array of DoseMatrix objects representing dose distributions to be downloaded.
     * @param {Image3D[]} selectedImages -
     *        An array of Image3D objects representing images to be downloaded.
     * @returns {Promise<void>} A promise that resolves when all downloads are completed.
     */
    const handleDownloadBatch = async (
        selectedImages: Image3D[] = [],
        selectedStructureSets: SelectedStructureSetsWithStructures[],
        selectedDoses: DoseMatrix[],
        exportToMonaco: boolean = false,
        selectedRegistrations: RigidRegistration[] = []
    ): Promise<void> => {
        try {
            setExporting(true);
            const totalItems =
                selectedStructureSets.length +
                selectedDoses.length +
                selectedImages.length +
                selectedRegistrations.length;

            // Single-dose-with-objectives must go through the zip path
            // because the JSON sidecar would otherwise trigger a separate
            // browser download alongside the .dcm files (and some browsers
            // block multiple consecutive saves).
            const singleDoseHasObjectives =
                selectedDoses.length === 1 &&
                (selectedDoses[0].optimizationObjectives?.length ?? 0) > 0;

            if (totalItems === 1 && !singleDoseHasObjectives) {
                if (selectedImages.length === 1) {
                    return await handleDownloadImage(selectedImages[0]);
                } else if (
                    selectedStructureSets.length === 1 &&
                    !exportToMonaco
                ) {
                    const { structureSetUid, selectedRoiIds } =
                        selectedStructureSets[0];
                    return await handleDownloadStructureSet(
                        structureSetUid,
                        selectedRoiIds
                    );
                } else if (selectedDoses.length === 1) {
                    return await handleDownloadDose(selectedDoses[0]);
                } else if (selectedRegistrations.length === 1) {
                    return await handleDownloadRegistration(
                        selectedRegistrations[0]
                    );
                }
            }

            // One flush before anything enters the archive: a failed flush
            // or a stale set downloads nothing.
            if (
                !ensureSetsWithUidsCurrent(
                    selectedStructureSets.map((s) => s.structureSetUid)
                )
            ) {
                return;
            }

            const timestamp = new Date()
                .toISOString()
                .replace(/[:.]/g, '-')
                .slice(0, -5);
            const filename = `dicom-export-${timestamp}`;

            const zip = new JSZip();

            for (const image of selectedImages) {
                const dir = `${image.seriesMetadata.modality}_${image.dicomUid}`;
                const blobs = Array.from(image.getSlicesAsDicomBlobs());
                blobs.forEach((blob, index) => {
                    zip.file(`${dir}/${index}.dcm`, blob);
                });
            }

            for (const {
                structureSetUid,
                selectedRoiIds,
            } of selectedStructureSets) {
                const structureSetLabel = getStructureSetLabel(structureSetUid);
                const blob = structureSetToBlob(
                    structureSetUid,
                    selectedRoiIds,
                    exportToMonaco
                );
                if (blob) {
                    zip.file(
                        buildStructureSetFilename(
                            structureSetLabel,
                            structureSetUid
                        ),
                        blob
                    );
                }

                if (exportToMonaco) {
                    const monacoBlob =
                        monacoStructureSetToBlob(structureSetUid);
                    if (monacoBlob) {
                        const monacoFilename = buildStructureSetFilename(
                            structureSetLabel,
                            structureSetUid
                        ).replace(/\.dcm$/, '.dir');
                        zip.file(monacoFilename, monacoBlob);
                    }
                }
            }

            for (const dose of selectedDoses) {
                const { doseBlob, planBlob } = getDoseBlobs(dose);
                if (doseBlob) {
                    zip.file(
                        buildRtDoseFilename(dose.plan?.label ?? dose.dicomUid),
                        doseBlob
                    );
                }
                if (planBlob) {
                    zip.file(
                        buildRtPlanFilename(dose.plan?.label ?? dose.dicomUid),
                        planBlob
                    );
                }
                if (
                    dose.optimizationObjectives &&
                    dose.optimizationObjectives.length > 0
                ) {
                    const json = buildOptimizationObjectivesJson(
                        dose.optimizationObjectives
                    );
                    zip.file(
                        buildOptimizationObjectivesFilename(
                            dose.plan?.label,
                            dose.dicomUid
                        ),
                        JSON.stringify(json, null, 2)
                    );
                    const csv = buildOptimizationObjectivesCsv(
                        dose.optimizationObjectives
                    );
                    zip.file(
                        buildOptimizationObjectivesFilename(
                            dose.plan?.label,
                            dose.dicomUid,
                            'csv'
                        ),
                        csv
                    );
                }
            }

            for (const registration of selectedRegistrations) {
                registration.syncTransformationToDataset();
                const blob = registration.dicomParent.getAsDicomBlob();
                const label =
                    registration.contentLabel || registration.dicomUid;
                zip.file(buildRegistrationFilename(label), blob);
            }

            // Generate and download the ZIP file
            const zipBlob = await zip.generateAsync({
                type: 'blob',
                compression: 'DEFLATE',
            });
            saveBlobToFile(zipBlob, `${filename}.zip`);
        } catch (error) {
            handleError(
                error,
                exportFailureMessage(error, 'Failed to download DICOM files')
            );
        } finally {
            setExporting(false);
        }
    };

    /**
     * Exports multiple selected `StructureSet`, `DoseMatrix`, and `Image3D` in a single batch upload.
     *
     * This function processes multiple objects,
     * extracts the corresponding DICOM files, and uploads them all in separate API requests.
     *
     * @param {SelectedStructureSetsWithStructures[]} selectedStructureSets -
     *        An array of objects identifying a structure set by SOP UID and its selected structures.
     * @param {DoseMatrix[]} selectedDoses -
     *        An array of DoseMatrix objects representing dose distributions to be exported.
     * @param {Image3D[]} selectedImages -
     *        An array of Image3D objects representing images to be exported.
     * @returns {Promise<void>} A promise that resolves when the upload process is completed.
     */
    const handleExportBatch = async (
        selectedImages: Image3D[] = [],
        selectedStructureSets: SelectedStructureSetsWithStructures[],
        selectedDoses: DoseMatrix[],
        targetName: string,
        selectedRegistrations: RigidRegistration[] = []
    ) => {
        try {
            setExporting(true);
            // A batch export marks every item afterwards; without a session
            // that throws once the whole batch is already uploaded, so the
            // user sees a failure for data that arrived. Fail up front.
            assertSessionForExport();
            // One flush before the first upload: a failed flush or a stale
            // set sends nothing.
            if (
                !ensureSetsWithUidsCurrent(
                    selectedStructureSets.map((s) => s.structureSetUid)
                )
            ) {
                return;
            }

            for (const image of selectedImages) {
                const blobs = Array.from(image.getSlicesAsDicomBlobs());
                for (let i = 0; i < blobs.length; i++) {
                    const blob = blobs[i];
                    await uploadDicomBlob(
                        blob,
                        `${image.seriesMetadata.modality}_${image.dicomUid}_${i}.dcm`,
                        targetName
                    );
                }
            }

            for (const {
                structureSetUid,
                selectedRoiIds,
            } of selectedStructureSets) {
                const structureSetLabel = getStructureSetLabel(structureSetUid);
                const blob = structureSetToBlob(
                    structureSetUid,
                    selectedRoiIds
                );
                if (blob) {
                    await uploadDicomBlob(
                        blob,
                        buildStructureSetFilename(
                            structureSetLabel,
                            structureSetUid
                        ),
                        targetName
                    );
                }
            }

            for (const dose of selectedDoses) {
                const { doseBlob, planBlob } = getDoseBlobs(dose);
                if (planBlob) {
                    await uploadDicomBlob(
                        planBlob,
                        buildRtPlanFilename(dose.plan?.label ?? dose.dicomUid),
                        targetName
                    );
                }
                if (doseBlob) {
                    await uploadDicomBlob(
                        doseBlob,
                        buildRtDoseFilename(dose.plan?.label ?? dose.dicomUid),
                        targetName
                    );
                }
            }

            for (const registration of selectedRegistrations) {
                registration.syncTransformationToDataset();
                const blob = registration.dicomParent.getAsDicomBlob();
                const label =
                    registration.contentLabel || registration.dicomUid;
                await uploadDicomBlob(
                    blob,
                    buildRegistrationFilename(label),
                    targetName
                );
            }

            selectedImages.forEach((image) => {
                markImageExported(image);
            });

            selectedStructureSets.forEach(({ structureSetUid }) => {
                markStructureSetExported(structureSetUid);
            });

            selectedDoses.forEach((dose) => {
                markDoseExported(dose);
            });

            selectedRegistrations.forEach((registration) => {
                markRegistrationExported(registration);
            });

            // Deliberately does NOT clear the unsaved-changes state: a
            // legacy-connector export is not a database save, and clearing
            // it here would silently disarm the guard for pending
            // connector-next changes. hasUnsavedChangesState is derived by
            // PendingChangesRecoilBridge and clears on save or unload.
            enqueueSnackbar('All selected DICOM files uploaded successfully', {
                variant: 'success',
            });
        } catch (error) {
            console.error('Error during batch export:', redactDicomUids(error));
            enqueueSnackbar('Failed to upload batch DICOM files', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };
    return {
        exporting,
        handleDownloadStructureSet,
        handleDownloadDose,
        handleDownloadRegistration,
        handleDownloadBatch,
        handleExportBatch,
    };
};
